import { useState, useCallback, useEffect } from 'react';
import { FormData, City, Warehouse } from '../types';
import { searchCities, getWarehouses, submitForm } from '../services/api';
import { validateForm, isFormValid } from '../utils/validation';

const initialFormData: FormData = {
  city: null,
  warehouse: null,
  name: '',
  phone: '',
};

// Function to get URL parameters
export const getUrlParameter = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

export const useForm = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [cities, setCities] = useState<City[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [cityQuery, setCityQuery] = useState('');
  const [warehouseQuery, setWarehouseQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);

  // Get amount from URL parameter if it exists
  const amountParam = getUrlParameter('amount');

  // Log state changes
  useEffect(() => {
    console.log('Form data updated:', formData);
  }, [formData]);

  useEffect(() => {
    console.log('Errors updated:', errors);
  }, [errors]);

  useEffect(() => {
    console.log('Cities updated:', cities);
  }, [cities]);

  useEffect(() => {
    console.log('Warehouses updated:', warehouses);
  }, [warehouses]);

  // Handle city search
  useEffect(() => {
    const fetchCities = async () => {
      if (cityQuery.length < 2) {
        console.log('City query too short, skipping API call');
        setCities([]);
        return;
      }

      console.log('Fetching cities for query:', cityQuery);
      setIsLoading(true);
      try {
        const results = await searchCities(cityQuery);
        console.log('Cities fetched:', results);
        setCities(results);
      } catch (error) {
        console.error('Error fetching cities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    console.log('Setting up city search timeout for query:', cityQuery);
    const timeoutId = setTimeout(fetchCities, 500);

    return () => {
      console.log('Clearing city search timeout');
      clearTimeout(timeoutId);
    };
  }, [cityQuery]);

  // Handle warehouse fetch when city changes or warehouse query changes
  useEffect(() => {
    const fetchWarehouses = async () => {
      if (!formData.city) {
        console.log('No city selected, skipping warehouse fetch');
        setWarehouses([]);
        return;
      }

      console.log('Fetching warehouses for city:', formData.city.Description);
      console.log('Warehouse query:', warehouseQuery);
      setIsLoading(true);
      try {
        const results = await getWarehouses(formData.city.Ref, warehouseQuery);
        console.log('Warehouses fetched:', results);
        setWarehouses(results);
      } catch (error) {
        console.error('Error fetching warehouses:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWarehouses();
  }, [formData.city, warehouseQuery]);

  // Handle input changes
  const handleInputChange = useCallback((name: string, value: any) => {
    console.log(`Input changed: ${name} =`, value);
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear warehouse when city changes
    if (name === 'city') {
      console.log('City changed, clearing warehouse selection');
      setFormData((prev) => ({ ...prev, warehouse: null }));
      setWarehouseQuery(''); // Reset warehouse query when city changes
    }

    // Clear error when field is updated
    setErrors((prev) => ({ ...prev, [name]: null }));
  }, []);

  // Handle city search input
  const handleCitySearch = useCallback((query: string) => {
    console.log('City search query changed:', query);
    setCityQuery(query);
  }, []);

  // Handle warehouse search input
  const handleWarehouseSearch = useCallback((query: string) => {
    console.log('Warehouse search query changed:', query);
    setWarehouseQuery(query);
  }, []);

  // Validate form fields
  const validateField = useCallback((name: keyof FormData) => {
    console.log(`Validating field: ${name}`);
    const validationResults = validateForm(formData);
    console.log(`Validation result for ${name}:`, validationResults[name]);
    setErrors((prev) => ({ ...prev, [name]: validationResults[name] }));
    return !validationResults[name]; // Return true if valid
  }, [formData]);

  // Validate current step
  const validateStep = useCallback((currentStep: number): boolean => {
    console.log(`Validating step ${currentStep}`);
    const validationResults = validateForm(formData);
    console.log('Step validation results:', validationResults);

    switch (currentStep) {
      case 1:
        setErrors((prev) => ({ ...prev, city: validationResults.city }));
        return !validationResults.city;
      case 2:
        setErrors((prev) => ({ ...prev, warehouse: validationResults.warehouse }));
        return !validationResults.warehouse;
      case 3:
        setErrors((prev) => ({
          ...prev,
          name: validationResults.name,
          phone: validationResults.phone
        }));
        return !validationResults.name && !validationResults.phone;
      default:
        return false;
    }
  }, [formData]);

  // Handle next step
  const handleNextStep = useCallback((skipValidation = false) => {
    console.log(`Attempting to move from step ${step} to ${step + 1}`);
    if (skipValidation || validateStep(step)) {
      console.log(`Moving to step ${step + 1}`);
      setStep((prevStep) => prevStep + 1);
    } else {
      console.log(`Validation failed for step ${step}, staying on current step`);
    }
  }, [formData, step, validateStep]);

  // Handle previous step
  const handlePrevStep = useCallback(() => {
    if (step > 1) {
      console.log(`Moving back from step ${step} to ${step - 1}`);
      setStep(step - 1);
    }
  }, [step]);

  // Get Monobank URL with parameters
  const getMonobankUrl = useCallback(() => {
    // Get amount and item from URL parameters
    const amount = getUrlParameter('amount');
    const item = getUrlParameter('item');

    // Create the Monobank URL with parameters
    const monoUrl = 'https://send.monobank.ua/2G5MMpBymq';
    const params = new URLSearchParams();

    if (amount) {
      params.append('amount', amount);
    }

    // Add the item parameter to the URL if it exists
    // We don't need to add it to the Monobank URL, but we'll keep it for reference
    if (item) {
      params.append('item', item);
    }

    const paramsString = params.toString();
    return paramsString ? `${monoUrl}?${paramsString}` : monoUrl;
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    console.log('Attempting to submit form');
    const validationResults = validateForm(formData);
    console.log('Final validation results:', validationResults);
    setErrors(validationResults);

    if (isFormValid(formData)) {
      console.log('Form is valid, proceeding with submission');
      setIsSubmitting(true);

      try {
        console.log('Submitting form data:', formData);
        const success = await submitForm(formData);

        if (success) {
          console.log('Form submitted successfully');
          setIsSuccess(true);
          // No auto-redirect anymore, user will choose payment method
        } else {
          console.log('Form submission failed');
          setErrors((prev) => ({
            ...prev,
            submit: 'Failed to submit the form. Please try again.'
          }));
        }
      } catch (error) {
        console.error('Error during form submission:', error);
        setErrors((prev) => ({
          ...prev,
          submit: 'An error occurred. Please try again.'
        }));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      console.log('Form validation failed, not submitting');
    }
  }, [formData]);

  // Handle popular city selection
  const handlePopularCitySelect = useCallback(async (cityName: string) => {
    console.log(`Popular city selected: ${cityName}`);
    setIsLoading(true);

    try {
      // Search for the city
      const foundCities = await searchCities(cityName);
      console.log('Cities found for popular city:', foundCities);

      if (foundCities && foundCities.length > 0) {
        // Find exact match or first result
        const exactMatch = foundCities.find(city =>
          city.Description.toLowerCase() === cityName.toLowerCase()
        );

        const cityToSelect = exactMatch || foundCities[0];
        console.log('Selected city from popular cities:', cityToSelect);

        // Set the city
        setFormData(prev => ({ ...prev, city: cityToSelect }));

        // Clear any city error
        setErrors(prev => ({ ...prev, city: null }));

        // Proceed to next step after a short delay
        setTimeout(() => {
          setStep(2);
        }, 300);
      }
    } catch (error) {
      console.error('Error selecting popular city:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    formData,
    errors,
    cities,
    warehouses,
    isLoading,
    isSubmitting,
    step,
    isSuccess,
    handleInputChange,
    handleCitySearch,
    handleWarehouseSearch,
    validateField,
    handleNextStep,
    handlePrevStep,
    handleSubmit,
    getMonobankUrl,
    handlePopularCitySelect,
  };
};